/**
 * vote-open-form (US-AIF.P3-0255-S01 · @UC9440 · @gate ai) — FORM MỞ PHIÊN BIỂU QUYẾT.
 *
 * USECASE: user mở phiên biểu quyết QUA CHAT cho HAI loại:
 *  - VOTE-EXPENSE:    biểu quyết một KHOẢN CHI đang chờ quyết định (nối `openVote` — engine hiện hữu).
 *  - VOTE-GOVERNANCE: quyết định QUẢN TRỊ phi-tiền (nối `createDecision` — engine hiện hữu).
 *
 * 🔴🔴 BẤT BIẾN CỐT LÕI (DP-3 — LÝ DO TASK NÀY TỒN TẠI): **AI KHÔNG SINH SỐ / KHÔNG SINH LUẬT.**
 * quorum · thresholdRatio · deadline · weightSnapshot · eligibleCount là LUẬT CỦA PHIÊN. Chúng được
 * SNAPSHOT TỪ CONFIG QUỸ/NHÓM lúc mở phiên bởi engine (`expense-decision.service.ts:698-767` đọc
 * `ConsensusF3ConfigService`/`VoteWeightConfigService`/`FundConfigService`; `governance-vote.service.ts:75-97`
 * đọc `GovernanceConfigService`). Form NÀY chỉ chọn **NỘI DUNG** (khoản chi nào / loại quyết định gì +
 * payload). KHÔNG có, và KHÔNG ĐƯỢC có, field nhập quorum/ngưỡng/hạn.
 *
 * Vì sao nghiêm ngặt: nếu form cho user (hoặc LLM) gõ "quorum = 1 người" thì một người tự mở phiên rồi
 * tự thông qua khoản chi ⇒ tiền rời quỹ mà nhóm không hề đồng thuận. Luật PHẢI đến từ config nhóm đã
 * thoả thuận trước, KHÔNG từ câu chat. `VOTE_FORBIDDEN_RULE_FIELD_KEYS` + test khoá bất biến này.
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now. options (khoản chi / thành viên) do SERVICE
 * nạp THẬT theo user — domain chỉ validate value ∈ options (chống IDOR ở LỚP domain; service re-verify
 * RBAC/tenant THẬT lúc confirm).
 */
import { DomainError } from "../errors.js";
import {
  validateFieldValue,
  type SlotFieldSpec,
  type SlotOption,
  type FormState,
} from "./form-slot-filling.js";

/** intentCode VOTE-EXPENSE (mở phiên biểu quyết cho một khoản chi). Khớp catalog + router EXACT. */
export const VOTE_EXPENSE_INTENT_CODE = "VOTE-EXPENSE";

/** intentCode VOTE-GOVERNANCE (mở phiên quyết định quản trị phi-tiền). Khớp catalog + router EXACT. */
export const VOTE_GOVERNANCE_INTENT_CODE = "VOTE-GOVERNANCE";

/** specVersion form VOTE-EXPENSE (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const VOTE_EXPENSE_SPEC_VERSION = 1;

/** specVersion form VOTE-GOVERNANCE. */
export const VOTE_GOVERNANCE_SPEC_VERSION = 1;

/** Key field chọn KHOẢN CHI cần mở phiên (single_choice trên khoản chi THẬT đang chờ quyết định). */
export const VOTE_EXPENSE_KEY = "expenseId";

/** Key field chọn LOẠI quyết định quản trị. */
export const VOTE_GOVERNANCE_KIND_KEY = "governanceKind";

/** Key field tiêu đề phiên governance (câu chữ user, hiển thị cho người bỏ phiếu). */
export const VOTE_GOVERNANCE_TITLE_KEY = "title";

/** Key field payload governance: khoá cấu hình/quy chế (config_change · rule_change). */
export const VOTE_GOVERNANCE_CONFIG_KEY_KEY = "configKey";

/** Key field payload governance: giá trị mới (config_change · rule_change). */
export const VOTE_GOVERNANCE_CONFIG_VALUE_KEY = "configValue";

/** Key field payload governance: chủ nhóm mới (owner_transfer — member_picker trên thành viên THẬT). */
export const VOTE_GOVERNANCE_NEW_OWNER_KEY = "newOwnerMemberId";

/**
 * Loại quyết định governance (mirror contracts `GOVERNANCE_KIND` — domain KHÔNG dep contracts, nhân bản
 * literal: đổi một bên đổi cả hai. Whitelist khớp CHECK DB `consensus.governanceKind`).
 */
export const VOTE_GOVERNANCE_KINDS = ["config_change", "rule_change", "owner_transfer"] as const;
export type VoteGovernanceKind = (typeof VOTE_GOVERNANCE_KINDS)[number];

/** Nhãn tiếng Việt cho từng loại quyết định — SERVER điền (KHÔNG để LLM bịa câu chữ quản trị). */
export const VOTE_GOVERNANCE_KIND_LABELS_VI: Readonly<Record<string, string>> = Object.freeze({
  config_change: "Đổi cấu hình nhóm",
  rule_change: "Đổi quy chế nhóm",
  owner_transfer: "Chuyển chủ nhóm",
});

/**
 * Trần độ dài — mirror contracts governance (`GOVERNANCE_TITLE_MAX` 200 · `GOVERNANCE_KEY_MAX` 64 ·
 * `GOVERNANCE_VALUE_MAX` 256). Domain chốt TRƯỚC ⇒ 400 sạch thay vì để zod biên ném muộn.
 */
export const VOTE_GOVERNANCE_TITLE_MAX = 200;
export const VOTE_GOVERNANCE_KEY_MAX = 64;
export const VOTE_GOVERNANCE_VALUE_MAX = 256;

/**
 * 🔴🔴 DANH SÁCH CẤM (DP-3 — bất biến "AI không sinh luật"): field-seq của MỌI form VOTE KHÔNG ĐƯỢC
 * chứa các key này. Chúng là LUẬT của phiên, engine SNAPSHOT từ config quỹ/nhóm lúc mở phiên.
 *
 * Đây KHÔNG phải danh sách trang trí: `assertNoRuleFields` chạy trên field-seq THẬT và test khoá cả hai
 * builder. Ai đó thêm `{ key: "quorumMin", type: "text" }` vào form ⇒ test ĐỎ ngay (mutation-proven).
 */
export const VOTE_FORBIDDEN_RULE_FIELD_KEYS: readonly string[] = Object.freeze([
  "quorum",
  "quorumMin",
  "threshold",
  "thresholdRatio",
  "deadline",
  "deadlineHours",
  "mechanism",
  "mode",
  "weightSource",
  "weightSnapshot",
  "eligibleCount",
  "totalEligibleWeight",
  "tieBreak",
  "silentApproveHours",
  "voteMaxOpenHours",
  "voteMinDeadlineHours",
]);

/** Thứ tự field VOTE-EXPENSE (tất-định). CHỈ chọn khoản chi — không luật, không tiền. */
export const VOTE_EXPENSE_FIELD_ORDER = [VOTE_EXPENSE_KEY] as const;

/**
 * Thứ tự field VOTE-GOVERNANCE (tất-định). `kind` TRƯỚC payload vì payload PHỤ THUỘC kind (step-wise —
 * mirror COL-TIERED tierLabel): owner_transfer ⇒ member_picker; config/rule_change ⇒ key + value.
 */
export const VOTE_GOVERNANCE_FIELD_ORDER = [
  VOTE_GOVERNANCE_KIND_KEY,
  VOTE_GOVERNANCE_TITLE_KEY,
] as const;

/** Payload VOTE-EXPENSE đã validate — nối `ExpenseDecisionService.openVote` (chỉ expenseId). */
export interface VoteExpensePayload {
  readonly expenseId: string;
}

/** Payload VOTE-GOVERNANCE đã validate — nối `GovernanceVoteService.createDecision`. */
export interface VoteGovernancePayload {
  readonly governanceKind: VoteGovernanceKind;
  readonly title: string;
  /** Khớp `createGovernanceDecisionRequestSchema.payload` (union theo kind — validate lại ở biên zod). */
  readonly payload:
    | { readonly key: string; readonly value: string }
    | { readonly newOwnerMemberId: string };
}

/**
 * 🔴🔴 CHỐT BẤT BIẾN DP-3: field-seq KHÔNG chứa field LUẬT (quorum/ngưỡng/hạn/...). Gọi trong CẢ HAI
 * builder ⇒ không thể ship một form vote có ô nhập luật (fail-loud ngay lúc dựng spec, không đợi runtime).
 * So khớp KHÔNG phân biệt hoa/thường để "QuorumMin"/"quorummin" cũng bị chặn.
 */
export function assertNoRuleFields(fields: readonly SlotFieldSpec[]): void {
  const forbidden = new Set(VOTE_FORBIDDEN_RULE_FIELD_KEYS.map((k) => k.toLowerCase()));
  for (const f of fields) {
    if (forbidden.has(f.key.toLowerCase())) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        `Field "${f.key}" là LUẬT của phiên biểu quyết — luật snapshot từ config quỹ, form KHÔNG được nhập.`,
      );
    }
    for (const col of f.columns ?? []) {
      if (forbidden.has(col.key.toLowerCase())) {
        throw new DomainError(
          "REQUIRED_FIELD_MISSING",
          `Cột "${col.key}" là LUẬT của phiên biểu quyết — luật snapshot từ config quỹ, form KHÔNG được nhập.`,
        );
      }
    }
  }
}

/**
 * Dựng chuỗi field VOTE-EXPENSE: CHỈ `expenseId` (single_choice trên khoản chi THẬT đang chờ quyết
 * định — service nạp options theo user/quỹ ở start/hydrateSpec). Catalog giữ options RỖNG (template
 * thuần). Immutable (copy options).
 */
export function buildVoteExpenseFields(expenseOptions: readonly SlotOption[]): SlotFieldSpec[] {
  const fields: SlotFieldSpec[] = [
    {
      key: VOTE_EXPENSE_KEY,
      type: "single_choice",
      required: true,
      options: [...expenseOptions],
    },
  ];
  assertNoRuleFields(fields);
  return fields;
}

/**
 * Dựng chuỗi field VOTE-GOVERNANCE theo `kind` ĐÃ chọn (step-wise — payload phụ thuộc kind):
 *  - kind chưa chọn ⇒ [kind, title] (hỏi loại + tiêu đề trước; field payload chưa dựng được).
 *  - owner_transfer ⇒ [kind, title, member_picker(newOwnerMemberId)].
 *  - config_change / rule_change ⇒ [kind, title, text(configKey), text(configValue)].
 *
 * `memberOptions` = thành viên ACTIVE THẬT của nhóm (service nạp) — chống IDOR ở LỚP domain.
 */
export function buildVoteGovernanceFields(
  memberOptions: readonly SlotOption[],
  kind?: string | null,
): SlotFieldSpec[] {
  const base: SlotFieldSpec[] = [
    {
      key: VOTE_GOVERNANCE_KIND_KEY,
      type: "single_choice",
      required: true,
      options: VOTE_GOVERNANCE_KINDS.map((k) => ({ value: k })),
    },
    { key: VOTE_GOVERNANCE_TITLE_KEY, type: "text", required: true },
  ];
  let fields: SlotFieldSpec[];
  if (kind === "owner_transfer") {
    fields = [
      ...base,
      {
        key: VOTE_GOVERNANCE_NEW_OWNER_KEY,
        type: "member_picker",
        required: true,
        options: [...memberOptions],
      },
    ];
  } else if (kind === "config_change" || kind === "rule_change") {
    fields = [
      ...base,
      { key: VOTE_GOVERNANCE_CONFIG_KEY_KEY, type: "text", required: true },
      { key: VOTE_GOVERNANCE_CONFIG_VALUE_KEY, type: "text", required: true },
    ];
  } else {
    fields = base;
  }
  assertNoRuleFields(fields);
  return fields;
}

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Thiếu thông tin bắt buộc: ${field}.`);
  }
  return value.trim();
}

/**
 * Validate formState VOTE-EXPENSE → payload (fail-fast). CHỈ chốt `expenseId` ∈ options (khoản chi THẬT
 * đang chờ quyết định của quỹ user — chống IDOR ở LỚP domain). KHÔNG chốt luật (engine snapshot config).
 */
export function validateVoteExpenseForm(
  formState: FormState,
  expenseOptions: readonly SlotOption[],
): VoteExpensePayload {
  const expenseId = assertPresent(formState[VOTE_EXPENSE_KEY], VOTE_EXPENSE_KEY);
  const validation = validateFieldValue(
    {
      key: VOTE_EXPENSE_KEY,
      type: "single_choice",
      required: true,
      options: [...expenseOptions],
    },
    expenseId,
  );
  if (!validation.ok) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Khoản chi được chọn không nằm trong danh sách khoản chi chờ quyết định của bạn.",
    );
  }
  return { expenseId: validation.value };
}

/**
 * Validate formState VOTE-GOVERNANCE → payload (fail-fast, KHÔNG partial).
 *
 * Thứ tự chốt (tất-định):
 *  1. kind ∈ whitelist (khớp CHECK DB — kind lạ ⇒ 400 TRƯỚC khi chạm engine).
 *  2. title non-empty ≤ trần (mirror contract .max(200)).
 *  3. payload theo kind:
 *     - owner_transfer ⇒ newOwnerMemberId ∈ memberOptions (thành viên ACTIVE THẬT — chống mở phiên bỏ
 *       phiếu cho người không tồn tại; engine fail-fast lần nữa `governance-vote.service.ts:79-86`).
 *     - config_change / rule_change ⇒ key ≤64 + value ≤256 (mirror contract).
 * KHÔNG chốt/nhận quorum/ngưỡng/hạn — luật snapshot từ config nhóm (DP-3).
 */
export function validateVoteGovernanceForm(
  formState: FormState,
  memberOptions: readonly SlotOption[],
): VoteGovernancePayload {
  // 1) kind ∈ whitelist.
  const kindRaw = assertPresent(formState[VOTE_GOVERNANCE_KIND_KEY], VOTE_GOVERNANCE_KIND_KEY);
  if (!(VOTE_GOVERNANCE_KINDS as readonly string[]).includes(kindRaw)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Loại quyết định không hợp lệ.",
    );
  }
  const governanceKind = kindRaw as VoteGovernanceKind;

  // 2) title non-empty + ≤ trần.
  const title = assertPresent(formState[VOTE_GOVERNANCE_TITLE_KEY], VOTE_GOVERNANCE_TITLE_KEY);
  if (title.length > VOTE_GOVERNANCE_TITLE_MAX) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tiêu đề quá dài (tối đa ${VOTE_GOVERNANCE_TITLE_MAX} ký tự).`,
    );
  }

  // 3) payload theo kind.
  if (governanceKind === "owner_transfer") {
    const newOwnerRaw = assertPresent(
      formState[VOTE_GOVERNANCE_NEW_OWNER_KEY],
      VOTE_GOVERNANCE_NEW_OWNER_KEY,
    );
    const validation = validateFieldValue(
      {
        key: VOTE_GOVERNANCE_NEW_OWNER_KEY,
        type: "member_picker",
        required: true,
        options: [...memberOptions],
      },
      newOwnerRaw,
    );
    if (!validation.ok) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Chủ nhóm mới không nằm trong danh sách thành viên của nhóm.",
      );
    }
    return { governanceKind, title, payload: { newOwnerMemberId: validation.value } };
  }

  const key = assertPresent(formState[VOTE_GOVERNANCE_CONFIG_KEY_KEY], VOTE_GOVERNANCE_CONFIG_KEY_KEY);
  if (key.length > VOTE_GOVERNANCE_KEY_MAX) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Khoá cấu hình quá dài (tối đa ${VOTE_GOVERNANCE_KEY_MAX} ký tự).`,
    );
  }
  const value = assertPresent(
    formState[VOTE_GOVERNANCE_CONFIG_VALUE_KEY],
    VOTE_GOVERNANCE_CONFIG_VALUE_KEY,
  );
  if (value.length > VOTE_GOVERNANCE_VALUE_MAX) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Giá trị quá dài (tối đa ${VOTE_GOVERNANCE_VALUE_MAX} ký tự).`,
    );
  }
  return { governanceKind, title, payload: { key, value } };
}
